import { createContext, JSX, ReactNode, useContext } from 'react';
import { AccountAllowances } from './hooks/customer-account-allowances';

export type AllowanceContextValue = {
    loadingAllowances: boolean,
    allowancesError: string,
    allowances: AccountAllowances|undefined,
    reloadAllowances: () => Promise<void>,
};

const empty: AllowanceContextValue = {
    loadingAllowances: false,
    allowancesError: '',
    allowances: undefined,
    reloadAllowances: async (): Promise<void> => {},
};

const CustomerAccountAllowanceContext = createContext<AllowanceContextValue>(empty);

/**
 * One read of the account's allowances, shared by every tab that can spend them.
 *
 * It only carries the value; the screen above it does the fetching, because the same
 * numbers are needed by the form's own validator and a second request for them would be
 * a second answer to the same question.
 *
 * It sits **outside** `TabbedForm` deliberately. `TabbedFormTabs` clones every direct
 * child of the form, so a provider placed among the tabs would be cloned as one; wrapping
 * the form instead leaves react-admin's own children untouched.
 */
export const CustomerAccountAllowanceProvider: (props: { value: AllowanceContextValue, children: ReactNode }) => JSX.Element =
    (props: { value: AllowanceContextValue, children: ReactNode }): JSX.Element => {
        return (
            <CustomerAccountAllowanceContext.Provider value={ props.value }>
                { props.children }
            </CustomerAccountAllowanceContext.Provider>
        );
    }

export const useCustomerAccountAllowances: () => AllowanceContextValue = (): AllowanceContextValue => {
    return useContext(CustomerAccountAllowanceContext);
}
